import { Button, Form, Image, Input, Select, Space, Switch, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { CmsApi, CmsHelp, CmsHelpCategory } from 'apis/cms';
import usePagination from 'commons/hooks/usePagination';
import LOCALS from 'commons/locals';
import { CMS_LANGUAGE_OPTION_LIST } from 'constant/language';
import i18n from 'i18n';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import formatTime from 'utils/formatTime';

export const showStatusOptionList = [
  {
    value: 1,
    label: i18n.t('enable'),
  },
  {
    value: 0,
    label: i18n.t('disable'),
  },
];

const useArticleListQueryParams = () => {
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [keyword, setKeyword] = useQueryState(
    'keyword',
    parseAsString.withDefault(''),
  );
  const [categoryIdList, setCategoryIdList] = useQueryState(
    'categoryIdList',
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );
  const [langList, setLangList] = useQueryState(
    'langList',
    parseAsArrayOf(parseAsString).withDefault([]),
  );
  const [showStatus, setShowStatus] = useQueryState(
    'showStatus',
    parseAsInteger,
  );

  return {
    pageNum,
    pageSize,
    keyword,
    categoryIdList,
    langList,
    showStatus,
    setPageNum,
    setPageSize,
    setKeyword,
    setCategoryIdList,
    setLangList,
    setShowStatus,
  };
};

const CmsArticleListPage = () => {
  const {
    pageNum,
    pageSize,
    keyword,
    categoryIdList,
    langList,
    showStatus,
    setPageNum,
    setPageSize,
    setKeyword,
    setCategoryIdList,
    setLangList,
    setShowStatus,
  } = useArticleListQueryParams();

  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<CmsHelp>();

  const [helpCategoryList, setHelpCategoryList] = useState<CmsHelpCategory[]>(
    [],
  );
  useEffect(() => {
    CmsApi.getHelpCategory().then((res) => {
      setHelpCategoryList(res);
    });
  }, []);

  const [form] = Form.useForm<{
    keyword?: string;
    langList?: string[];
    categoryIdList?: number[];
    showStatus?: number;
  }>();

  const getDataSource = useCallback(async () => {
    try {
      setLoading(true);
      const { list, total } = await CmsApi.getHelpList({
        pageNum,
        pageSize,
        keyword: keyword || undefined,
        categoryIdList: categoryIdList.length
          ? categoryIdList.join(',')
          : undefined,
        langList: langList.length ? langList.join(',') : undefined,
        showStatus: showStatus ?? undefined,
      });

      setDataSource(list);
      setTotal(total);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, [
    categoryIdList,
    keyword,
    langList,
    pageNum,
    pageSize,
    setDataSource,
    setLoading,
    setTotal,
    showStatus,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      keyword,
      categoryIdList,
      langList,
      showStatus: showStatus ?? undefined,
    });
  }, [categoryIdList, form, keyword, langList, showStatus]);

  useEffect(() => {
    getDataSource();
  }, [getDataSource]);

  const onFinish = useCallback(
    (values: {
      keyword?: string;
      langList?: string[];
      categoryIdList?: number[];
      showStatus?: number;
    }) => {
      setKeyword(values.keyword || '');
      setCategoryIdList(
        values.categoryIdList?.length ? values.categoryIdList : null,
      );
      setLangList(values.langList?.length ? values.langList : null);
      setShowStatus(values.showStatus ?? null);
      setPageNum(1);
    },
    [setCategoryIdList, setKeyword, setLangList, setPageNum, setShowStatus],
  );

  const onReset = useCallback(() => {
    setKeyword('');
    setCategoryIdList(null);
    setLangList(null);
    setShowStatus(null);
    setPageNum(1);
    setPageSize(10);
  }, [
    setCategoryIdList,
    setKeyword,
    setLangList,
    setPageNum,
    setPageSize,
    setShowStatus,
  ]);

  const columns: ColumnsType<CmsHelp> = useMemo(() => {
    return [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
      },
      {
        title: i18n.t('article_category'),
        dataIndex: 'categoryId',
        key: 'categoryId',
        render(categoryId: string) {
          return (
            helpCategoryList.find((i) => i.id === Number(categoryId))?.name ||
            '-'
          );
        },
      },
      {
        title: i18n.t('path'),
        dataIndex: 'path',
        key: 'path',
        render(path?: string) {
          return path || '-';
        },
      },
      {
        title: i18n.t('cover'),
        dataIndex: 'icon',
        key: 'icon',
        render(icon?: string) {
          if (!icon) return '-';
          return <Image src={icon} className="w-24" />;
        },
      },
      {
        title: i18n.t('sort'),
        dataIndex: 'sort',
        key: 'sort',
        render(sort?: number) {
          return sort || '-';
        },
      },
      {
        title: i18n.t('title'),
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: i18n.t('language'),
        dataIndex: 'language',
        key: 'language',
        render: (language: string) => {
          return (
            CMS_LANGUAGE_OPTION_LIST.find((i) => i.value === language)?.label ||
            '-'
          );
        },
      },
      {
        title: i18n.t('article_publish_status'),
        dataIndex: 'showStatus',
        key: 'showStatus',
        render: (showStatus: 0 | 1 | null, { id }: CmsHelp) => {
          return (
            <Switch
              checked={showStatus === 1}
              onChange={async (checked) => {
                if (checked) {
                  await CmsApi.helpPublish(id);
                } else {
                  await CmsApi.helpUnPublish(id);
                }

                getDataSource();
              }}
            ></Switch>
          );
        },
      },
      {
        title: i18n.t('created_time'),
        dataIndex: 'createTime',
        key: 'createTime',
        render(createTime: string) {
          return formatTime(createTime);
        },
      },
      {
        title: i18n.t('updated_time'),
        dataIndex: 'modifyTime',
        key: 'modifyTime',
        render(modifyTime?: string) {
          return formatTime(modifyTime);
        },
      },
      {
        key: 'options',
        title: <Trans i18nKey={LOCALS.options} />,
        fixed: 'right',
        width: 220,
        render: ({ id }: CmsHelp) => {
          return (
            <div>
              <Button
                type="link"
                onClick={() => {
                  window.location.href = `/cms/article-detail?id=${id}`;
                }}
              >
                <Trans i18nKey={LOCALS.edit} />
              </Button>

              <Button
                type="link"
                danger
                onClick={async () => {
                  await CmsApi.helpDelete(id);
                  getDataSource();
                }}
              >
                <Trans i18nKey={LOCALS.delete} />
              </Button>
            </div>
          );
        },
      },
    ];
  }, [getDataSource, helpCategoryList]);

  return (
    <div>
      <Form form={form} onFinish={onFinish} layout="inline">
        <Form.Item name="keyword" label={<Trans i18nKey={LOCALS.keyword} />}>
          <Input placeholder={i18n.t(LOCALS.please_enter) || ''} />
        </Form.Item>

        <Form.Item
          name="categoryIdList"
          label={i18n.t('article_category')}
          className="w-60"
        >
          <Select
            mode="multiple"
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={helpCategoryList.map(({ id: value, name: label }) => {
              return {
                value,
                label,
              };
            })}
          />
        </Form.Item>

        <Form.Item name="langList" label={i18n.t('language')}>
          <Select
            mode="multiple"
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={CMS_LANGUAGE_OPTION_LIST.map(({ value, label }) => {
              return {
                value,
                label,
              };
            })}
          />
        </Form.Item>

        <Form.Item
          name="showStatus"
          label={i18n.t('article_publish_status')}
        >
          <Select
            allowClear
            placeholder={i18n.t(LOCALS.please_select) || ''}
            style={{ minWidth: 120 }}
            options={showStatusOptionList}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              <Trans i18nKey={LOCALS.search} />
            </Button>

            <Button htmlType="button" onClick={onReset}>
              <Trans i18nKey={LOCALS.reset} />
            </Button>
            <Button
              onClick={() => {
                window.location.href = '/cms/article-detail';
              }}
            >
              <Trans i18nKey={LOCALS.add} />
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        tableLayout="fixed"
        bordered
        pagination={{
          total,
          pageSize,
          current: pageNum,
          onChange: (page, nextPageSize) => {
            setPageNum(page);
            setPageSize(nextPageSize);
          },
        }}
        loading={loading}
        rowKey={'id'}
        style={{
          marginTop: 12,
        }}
        dataSource={dataSource}
        columns={columns}
      />
    </div>
  );
};

export default CmsArticleListPage;
